<template>
  <div class="calling-dialog">
    <!-- 遠端音訊（始終啟用） -->
    <audio
      ref="remoteAudio"
      autoplay
    />

    <!-- 視訊元素（始終在 DOM 確保 ref 有效；移至 __video-area 內由父層 v-show 控制顯示） -->

    <!-- 接通前畫面 -->
    <div v-show="!isVideoMode" class="calling-dialog__content-wrap">
      <div class="calling-dialog__audio-body">
        <div class="calling-dialog__avatar">
          <i class="el-icon-user" />
        </div>
        <p class="calling-dialog__callee-name">
          {{ calleeDisplayName }}
        </p>
        <img
          v-if="callState === 'dialing' || callState === 'ringing'"
          class="calling-dialog__calling-animation"
          src="@/assets/img/intercom/calling-animation.gif"
          alt=""
        />
        <span class="calling-dialog__status-text-wrap">
          <p class="calling-dialog__status-text">
            {{ statusText }}
          </p>
        </span>
        <p
          v-if="callState === 'connected'"
          class="calling-dialog__timer"
        >
          {{ formattedDuration }}
        </p>
      </div>

      <div class="calling-dialog__controls">
        <div class="calling-dialog__control-wrap">
          <div class="calling-dialog__ctrl-item" @click="onToggleMute">
            <div class="calling-dialog__ctrl-btn">
              <img :src="!isMuted ? require('@/assets/img/intercom/mic-on@3x.png') : require('@/assets/img/intercom/mic-off@3x.png')" alt="" />
            </div>
            <span>{{ $t('Intercom.CloudIntercom.CallingDialog.Microphone') }}</span>
          </div>
        </div>

        <!-- 掛斷 -->
        <div class="calling-dialog__control-wrap">
          <div class="calling-dialog__ctrl-item">
            <div class="calling-dialog__ctrl-btn calling-dialog__ctrl-btn--hangup" @click="onHangup">
              <img src="@/assets/img/intercom/hang-up@3x.png" alt="" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 接通後畫面 -->
    <div v-show="isVideoMode" class="calling-dialog__content-wrap">
      <!-- 頂部資訊列 -->
      <div class="calling-dialog__video-top">
        <div class="calling-dialog__video-top-info">
          <p class="calling-dialog__callee-name">
            {{ calleeDisplayName }}
          </p>
          <p class="calling-dialog__timer">
            {{ formattedDuration }}
          </p>
        </div>
        <div class="calling-dialog__video-top-actions">
          <button
            class="calling-dialog__switch-btn"
            @click="switchCamera"
          >
            <i class="el-icon-refresh" />
            <span>{{ $t('Intercom.CloudIntercom.CallingDialog.SwitchCamera') }}</span>
          </button>
        </div>
      </div>

      <!-- 視訊畫面區塊 -->
      <div class="calling-dialog__video-area">
        <div class="calling-dialog__video calling-dialog__video--main">
          <video
            v-show="!isRemoteVideoOff"
            ref="remoteVideo"
            class="calling-dialog__remote-video"
            autoplay
            playsinline
          />
          <div
            v-if="isRemoteVideoOff"
            class="calling-dialog__cam-off calling-dialog__cam-off--main"
          >
            <img src="@/assets/img/intercom/camera-unclear@3x.png" />
            <span>{{ $t('Intercom.CloudIntercom.CallingDialog.RemoteVideoPlaceholder') }}</span>
          </div>
        </div>
        <div class="calling-dialog__video calling-dialog__video--pip">
          <video
            v-show="!isVideoOff"
            ref="localVideo"
            class="calling-dialog__local-video"
            autoplay
            playsinline
            muted
          />
          <div
            v-if="isVideoOff"
            class="calling-dialog__cam-off"
          >
            <img src="@/assets/img/intercom/camera-close@3x.png" />
            <span>{{ $t('Intercom.CloudIntercom.CallingDialog.LocalVideoPlaceholder') }}</span>
          </div>
        </div>
      </div>

      <!-- 底部控制列 -->
      <div class="calling-dialog__controls calling-dialog__controls--video">
        <div class="calling-dialog__video-ctrl-group">
          <div class="calling-dialog__ctrl-item" @click="onToggleVideo">
            <div class="calling-dialog__ctrl-btn">
              <img :src="!isVideoOff ? require('@/assets/img/intercom/camera-open@3x.png') : require('@/assets/img/intercom/camera-close@3x.png')" alt="" />
            </div>
            <span>{{ $t('Intercom.CloudIntercom.CallingDialog.Camera') }}</span>
          </div>
          <div class="calling-dialog__ctrl-item" @click="onToggleMute">
            <div class="calling-dialog__ctrl-btn">
              <img :src="!isMuted ? require('@/assets/img/intercom/mic-on@3x.png') : require('@/assets/img/intercom/mic-off@3x.png')" alt="" />
            </div>
            <span>{{ $t('Intercom.CloudIntercom.CallingDialog.Microphone') }}</span>
          </div>
        </div>
        <!-- 掛斷 -->
        <div class="calling-dialog__ctrl-btn calling-dialog__ctrl-btn--hangup" @click="onHangup">
          <img src="@/assets/img/intercom/hang-up@3x.png" alt="" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CALL_STATE } from '@/js/utils/sipClient'

export default {
  name: 'CallingDialog',
  props: {
    calleeUsername: {
      type: String,
      required: true
    },
    calleeDisplayName: {
      type: String,
      default: ''
    },
    // CalleesPage 負責建立並 init，CallingDialog 透過此 prop 取用
    sipClient: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      // 目前通話狀態，對應 CALL_STATE 枚舉
      callState: CALL_STATE.IDLE,
      // 本地麥克風是否靜音
      isMuted: false,
      // 本地攝影機是否已關閉（預設關閉，通話建立後才靜音視訊軌道）
      isVideoOff: true,
      // 遠端尚未送出視訊畫面（stream 未到達或對方鏡頭未開啟）
      isRemoteVideoOff: true,
      // 通話已連線的秒數
      durationSeconds: 0,
      // setInterval 計時器 ID
      durationTimer: null,
      // 是否處於視訊模式（false = 純語音，true = 視訊）
      isVideoMode: false,
      // 通話失敗且已顯示錯誤 alert，抑制 1.5s 自動關閉
      hasCallError: false,
      // 遠端視訊像素亮度定時檢查的 timer handle
      remoteVideoCheckTimer: null
    }
  },
  computed: {
    statusText () {
      const map = {
        [CALL_STATE.IDLE]: '',
        [CALL_STATE.DIALING]: this.$t('Intercom.CloudIntercom.CallingDialog.Dialing'),
        [CALL_STATE.RINGING]: this.$t('Intercom.CloudIntercom.CallingDialog.Dialing'),
        [CALL_STATE.CONNECTED]: this.$t('Intercom.CloudIntercom.CallingDialog.Connected'),
        [CALL_STATE.ENDED]: this.$t('Intercom.CloudIntercom.CallingDialog.Ended')
      }
      return map[this.callState] || ''
    },
    formattedDuration () {
      const m = Math.floor(this.durationSeconds / 60).toString().padStart(2, '0')
      const s = (this.durationSeconds % 60).toString().padStart(2, '0')
      return `${m}:${s}`
    }
  },
  mounted () {
    // 全屏通話介面開啟時，鎖定 body 捲動，防止使用者滑動到下方主頁面
    document.body.style.overflowY = 'hidden'
    this.sipClient.setCallbacks({
      onCallState: (state, errorCode, cause) => {
        this.callState = state
        if (state === CALL_STATE.CONNECTED) {
          this.startDurationTimer()
          // 接通後自動切換至視訊模式，攝影機預設開啟
          this.isVideoMode = true
          this.isVideoOff = false
        } else if (state === CALL_STATE.ENDED) {
          this.stopDurationTimer()
          if (errorCode === 400005) {
            // guestToken 已失效，委派給 CalleesPage 的 handleGuestTokenExpired 統一處理
            this.hasCallError = true
            this.$emit('guest-token-expired')
          } else if (errorCode) {
            this.hasCallError = true
            this.showCallError(errorCode)
            // 備援：alert 若未正常 resolve，一段時間後強制關閉；guard 避免與 showCallError 重複觸發
            setTimeout(() => { if (this.hasCallError) this.$emit('close') }, 2000)
          } else {
            // 無 X-Error-Code 時，依 JsSIP cause 判斷是否為已知錯誤（對應 callModule event.message fallback）
            const causeMessage = this.getCauseErrorMessage(cause)
            if (causeMessage) {
              this.hasCallError = true
              this.showCauseError(causeMessage)
              setTimeout(() => { if (this.hasCallError) this.$emit('close') }, 2000)
            } else {
              setTimeout(() => this.$emit('close'), 1000)
            }
          }
        }
      },
      onRemoteStream: (kind, stream) => {
        this.$nextTick(() => {
          if (kind === 'audio') {
            this.$refs.remoteAudio.srcObject = stream
          } else if (kind === 'video') {
            this.$refs.remoteVideo.srcObject = stream
            this.isRemoteVideoOff = false
            this.startRemoteVideoCheck()
          }
        })
      },
      onLocalStream: (stream) => {
        this.$nextTick(() => {
          this.$refs.localVideo.srcObject = stream
        })
      }
    })
    this.sipClient.call(this.calleeUsername)
  },
  beforeDestroy () {
    // 通話介面關閉時，還原 body 捲動（空字串回到 CSS 預設值）
    document.body.style.overflowY = ''
    this.stopDurationTimer()
    this.stopRemoteVideoCheck()
    this.sipClient.setCallbacks({})
  },
  methods: {
    /**
     * 開始計時，每秒遞增 durationSeconds；若 expireAtUtc 已過期則自動掛斷
     */
    startDurationTimer () {
      this.durationSeconds = 0
      this.durationTimer = setInterval(() => {
        this.durationSeconds++
      }, 1000)
    },

    /**
     * 停止計時並清除 timer
     */
    stopDurationTimer () {
      if (this.durationTimer) {
        clearInterval(this.durationTimer)
        this.durationTimer = null
      }
    },

    /**
     * 啟動遠端視訊亮度定期偵測。
     * 因 track.enabled = false 不觸發 onmute 且黑畫面仍有 RTP 封包，
     * 改用 Canvas 每 2 秒取樣 remoteVideo 的 8×8 縮圖並計算平均像素亮度：
     *   - 亮度 < 10（幾乎全黑）→ isRemoteVideoOff = true（鏡頭關閉）
     *   - 亮度 >= 10            → isRemoteVideoOff = false（有實際畫面）
     */
    startRemoteVideoCheck () {
      this.stopRemoteVideoCheck()
      this.remoteVideoCheckTimer = setInterval(() => {
        const video = this.$refs.remoteVideo
        if (!video || !video.videoWidth || !video.videoHeight) return
        try {
          const canvas = document.createElement('canvas')
          canvas.width = 8
          canvas.height = 8
          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0, 8, 8)
          const { data } = ctx.getImageData(0, 0, 8, 8)
          let brightness = 0
          for (let i = 0; i < data.length; i += 4) {
            brightness += data[i] + data[i + 1] + data[i + 2]
          }
          this.isRemoteVideoOff = brightness / (64 * 3) < 10
        } catch { /* canvas 取樣失敗時不更新狀態 */ }
      }, 2000)
    },

    /** 停止遠端視訊亮度偵測計時器 */
    stopRemoteVideoCheck () {
      if (this.remoteVideoCheckTimer) {
        clearInterval(this.remoteVideoCheckTimer)
        this.remoteVideoCheckTimer = null
      }
    },

    /**
     * 依錯誤碼顯示對應的通話失敗說明，使用者確認後關閉 CallingDialog
     * @param {number} errorCode - SIP X-Error-Code
     */
    showCallError (errorCode) {
      /**
       * 403009: 該戶別被移除部分授權
       * 404002: 該戶別被移除軟體的可撥打名單
       * 400003: 對方所有裝置皆開啟勿擾
       */
      let title
      if ([403009, 404002].includes(errorCode)) {
        title = this.$t('Intercom.CloudIntercom.CallingDialog.ErrorTitleUnauthorized')
      } else if (errorCode === 400003) {
        title = this.$t('Intercom.CloudIntercom.CallingDialog.ErrorTitleDoNotDisturb')
      } else {
        title = this.$t('Intercom.CloudIntercom.CallingDialog.ErrorTitle')
      }

      this.$alert(
        this.getCallErrorMessage(errorCode),
        title,
        {
          confirmButtonText: this.$t('Intercom.CloudIntercom.Common.Confirm'),
          type: 'warning',
          showClose: false,
          roundButton: true,
          closeOnClickModal: false,
          customClass: 'ci-alert'
        }
      ).then(() => { this.hasCallError = false; this.$emit('close') })
        .catch(() => { this.hasCallError = false; this.$emit('close') })
    },

    /**
     * 依 SIP X-Error-Code 回傳對應的使用者說明文案
     * @param {number} errorCode
     * @returns {string}
     */
    getCallErrorMessage (errorCode) {
      const map = {
        400003: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorCalleeAllInactive'),
        403008: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorPermissionDenied'),
        403009: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorAdminUnauth'),
        404002: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorAdminUnauth'),
        404005: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorCalleeNotActivated'),
        408001: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorCalleeNoResponse'),
        486001: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorCalleeBusy'),
        486002: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorCallerBusy'),
        603001: this.$t('Intercom.CloudIntercom.CallingDialog.ErrorCalleeNoResponse')
      }
      return map[errorCode] || this.$t('Intercom.CloudIntercom.CallingDialog.ErrorUnknown', { errorCode })
    },

    /**
     * 依 JsSIP cause 回傳對應的使用者說明文案；無對應則回傳 null，走正常關閉流程
     * @param {string} cause - JsSIP event.cause
     * @returns {string|null}
     */
    getCauseErrorMessage (cause) {
      const map = {
        'Request Timeout': this.$t('Intercom.CloudIntercom.CallingDialog.ErrorCalleeNoResponse'),
        'SIP Failure Code': this.$t('Intercom.CloudIntercom.CallingDialog.ErrorServerError'),
        'Not Found': this.$t('Intercom.CloudIntercom.CallingDialog.ErrorNumberNotFound')
      }
      return map[cause] || null
    },

    /**
     * 顯示 cause-based 錯誤訊息，使用者確認後關閉 CallingDialog
     * @param {string} message - 顯示的錯誤內容
     */
    showCauseError (message) {
      this.$alert(
        message,
        this.$t('Intercom.CloudIntercom.CallingDialog.ErrorTitle'),
        {
          confirmButtonText: this.$t('Intercom.CloudIntercom.Common.Confirm'),
          type: 'warning',
          showClose: false,
          roundButton: true,
          closeOnClickModal: false,
          customClass: 'ci-alert'
        }
      ).then(() => { this.hasCallError = false; this.$emit('close') })
        .catch(() => { this.hasCallError = false; this.$emit('close') })
    },

    /**
     * 切換本地攝影機鏡頭方向（前置 ↔ 後置）
     */
    async switchCamera () {
      if (!this.sipClient) return
      await this.sipClient.switchCamera()
    },

    /**
     * 切換本地攝影機開關，並同步更新 isVideoOff 狀態
     */
    onToggleVideo () {
      if (!this.sipClient) return
      this.isVideoOff = this.sipClient.toggleVideo()
    },

    /**
     * 切換本地麥克風靜音，並同步更新 isMuted 狀態
     */
    onToggleMute () {
      if (!this.sipClient) return
      this.isMuted = this.sipClient.toggleMute()
    },

    /**
     * 掛斷通話，通話結束後由 onCallState(ENDED) 觸發 close 事件
     */
    onHangup () {
      if (this.sipClient) {
        this.sipClient.hangup()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
*, *:before, *:after {
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

.calling-dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh; // fallback for browsers without dvh support
  height: 100dvh;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  backdrop-filter: blur(12px);
  background-color: rgba(0, 0, 0, 0.7);

  &__content-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  // 視訊畫面區塊：撐開頂部列與底部控制列之間的剩餘空間
  &__video-area {
    flex: 1;
    position: relative;
    overflow: hidden;
    background-color: #000;
  }

  // 視訊元素：定位相對於 __video-area
  &__video {
    position: absolute;
    object-fit: cover;

    &--main {
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
    }

    &--pip {
      top: 12px;
      left: 12px;
      width: 90px;
      height: 120px;
      border-radius: 8px;
      overflow: hidden;
      background-color: #333;
      z-index: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  // 遠端 / 本地視訊填滿各自的容器
  &__remote-video,
  &__local-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  // 鏡頭關閉時的佔位（PIP 與主畫面共用基礎樣式）
  &__cam-off {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background-color: #1a1a1a;

    img {
      width: 32px;
      height: 32px;
    }

    span {
      font-size: map-get($fontSize,x-small);
      color: #fff;
      text-align: center;
      white-space: pre-line;
    }

    // 主畫面（遠端）專屬覆寫
    &--main {
      gap: 24px;

      img {
        width: 48px;
        height: 48px;
      }

      span {
        font-size: map-get($fontSize,small);
      }
    }
  }

  // 純語音模式：頭像 + 名稱 + 狀態
  &__audio-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 65%;
    padding: 12vh 20px 0;
  }

  &__avatar {
    width: 108px;
    height: 108px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 20px;
  }

  &__callee-name {
    font-size: map-get($fontSize,medium);
    text-align: center;
    font-weight: 600;
    color: #fff;
    margin: 0;
  }

  &__calling-animation {
    width: 150px;
    height: 30px;
    margin-bottom: 24px;
    display: block;
    transform: scale(0.35);
  }

  &__status-text-wrap {
    padding: 7px 41px 8px;
    border-radius: 4px;
    background-color: #1f1f1f;
  }

  &__status-text {
    font-size: map-get($fontSize,small);
    color: rgba(255, 255, 255, 0.75);
    margin: 0;
  }

  &__timer {
    font-size: map-get($fontSize,small);
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
    font-variant-numeric: tabular-nums;
  }

  // 底部控制列（語音 / 視訊共用結構）
  &__controls {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 35%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-bottom: env(safe-area-inset-bottom);

    &--video {
      height: calc(135px + env(safe-area-inset-bottom));
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-start;
      background: rgba(0, 0, 0, 1);
      padding: 26px 16px;
      padding-bottom: calc(26px + env(safe-area-inset-bottom));
    }
  }

  &__control-wrap {
    display: flex;
    align-items: center;
    gap: 72px;

    &:not(:last-child) {
      margin-bottom: 40px;
    }
  }

  // 控制按鈕 + 文字標籤的包裝層
  &__ctrl-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;

    > span {
      font-size: map-get($fontSize,x-small);
      color: #fff;
      white-space: nowrap;
    }
  }

  // 視訊模式底部：鏡頭 + 麥克風群組
  &__video-ctrl-group {
    display: flex;
    gap: 24px;
  }

  &__ctrl-btn {
    width: 36px;
    height: 36px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }

    &--hangup {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      overflow: hidden;
    }

    & img {
      width: 100%;
      height: 100%
    }
  }

  // 視訊模式頂部資訊列
  &__video-top {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 90px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: rgba(0, 0, 0, 1);
  }

  &__video-top-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0; // flex child 預設 min-width: auto，會阻止收縮導致 ellipsis 失效
    padding-right: 12px;

    .calling-dialog__callee-name {
      font-size: map-get($fontSize,x-large);
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 8px;
    }
    .calling-dialog__timer {
      font-size: map-get($fontSize,small);
      color: #999;
    }
  }

  &__video-top-actions {
    flex-shrink: 0;
  }

  &__switch-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    color: #fff;
    cursor: pointer;
    font-size: map-get($fontSize,xxxx-large);
    padding: 0;

    > span { font-size: map-get($fontSize,x-small); }
  }
}

</style>
